import React, {useEffect, useCallback, ReactNode} from "react";
import CloseIcon from "../assets/close-icon.svg?react";

interface DialogProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    headerContent?: ReactNode;
    children: ReactNode;
    maxWidth?: string;
    showCloseButton?: boolean;
}

export function Dialog({
                           open,
                           onClose,
                           title,
                           headerContent,
                           children,
                           maxWidth = "560px",
                           showCloseButton = true,
                       }: DialogProps) {
    const onKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        },
        [onClose]
    );

    useEffect(() => {
        if (!open) return;
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [open, onKeyDown]);

    if (!open) return null;

    return (
        <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-1000 flex items-center sm:items-center justify-center"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="absolute inset-0 bg-black/40"/>

            <div
                className="relative z-1001 w-full bg-white rounded-3xl p-4 sm:p-5 shadow-xl m-0 sm:m-4"
                style={{maxWidth}}
            >
                {(title || headerContent || showCloseButton) && (
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            {title && (
                                <h2 className="text-xl sm:text-2xl font-semibold mb-2">{title}</h2>
                            )}
                            {headerContent}
                        </div>

                        {showCloseButton && (
                            <button
                                onClick={onClose}
                                className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
                                aria-label="Close"
                            >
                                <CloseIcon className="w-5 h-5"/>
                            </button>
                        )}
                    </div>
                )}

                <div className={title || headerContent ? "mt-4" : ""}>{children}</div>
            </div>
        </div>
    );
}
